import { Injectable } from '@angular/core';

import { VBrick360UtilService } from '../threeSixty/VBrick360Util.Service';
import { VgConferenceDualHlsService } from '../hls/dual/VgConferenceDualHls.Service';
import { VgHlsSupportService } from '../hls/VgHlsSupport.Service';

import { VideogularComponent } from './videogular/Videogular.Component';

import { HlsUtilService } from './HlsUtil.Service';
import { IVbSource } from './IVbSource';
import { NativePlaybackUtilService } from './NativePlaybackUtil.Service';
import { PlaybackOption } from './PlaybackOption';
import { VBrickPlayerComponent } from './VbrickPlayer.Component';
import { userAgentUtil } from './UserAgentUtil';

@Injectable()
export class SupportedPlaybacksService {
	constructor(
		private HlsUtilService: HlsUtilService,
		private NativePlaybackUtil: NativePlaybackUtilService,
		private VBrick360Util: VBrick360UtilService,
		private VG_HLS_SUPPORT: VgHlsSupportService,
		private vgConferenceDualHlsService: VgConferenceDualHlsService
	) {}

	//Todo - flashDisabled is always true. will clean it up later
	public getIsFlashPlayback(source: IVbSource, flashDisabled: boolean, vgApi: VideogularComponent): boolean { // TODO: leverage service(s) OR flag in flash plugin loader OR require the plugin to inspect flag
		const isNativeSupport: boolean = this.NativePlaybackUtil.canPlayNatively(source);
		const isVgHlsSupport: boolean = this.VG_HLS_SUPPORT.isMSE &&
			this.HlsUtilService.isHLS(source.src, source.type) &&
			!this.HlsUtilService.isMixedContent(source.src);

		return !flashDisabled &&
			!isNativeSupport &&
			!isVgHlsSupport;
	}

	public getSupportedPlaybackOptions(sourceInputs: IVbSource[], isFlashDisabled: boolean, forceFlashHlsPlayback?: boolean): PlaybackOption[] {
		if (!Array.isArray(sourceInputs)) {
			return [];
		}

		return this.shapeSupportedOptions(
			this.getFilteredSourceInputs(sourceInputs, isFlashDisabled, forceFlashHlsPlayback)
		);
	}

	public isPlaybackSupported(sourceInputs: IVbSource[], isFlashDisabled: boolean, forceFlashHlsPlayback?: boolean): boolean {
		return this.getFilteredSourceInputs(sourceInputs, isFlashDisabled, forceFlashHlsPlayback).length > 0;
	}

	//Todo - flashDisabled is always true. will clean it up later
	public isFlashHlsPlaybackUnsupported(activeSource: PlaybackOption, flashDisabled: boolean): boolean {
		const is360Source: boolean = activeSource.is360Enabled;
		const isRevConnect: boolean = this.HlsUtilService.isRevConnect(activeSource);

		return is360Source ||
			flashDisabled ||
			isRevConnect;
	}

	public isMulticastSource(src: string, type: string): boolean {
		return this.HlsUtilService.isMulticast(src, type) ||
			this.vgConferenceDualHlsService.isMulticast(src, type);
	}

	private getFilteredSourceInputs(sourceInputs: IVbSource[], isFlashDisabled: boolean, forceFlashHlsPlayback: boolean): IVbSource[] {
		return sourceInputs
			.filter(source => this.onlySupportedPlaybackTypes(source, isFlashDisabled, forceFlashHlsPlayback) && this.only360IfSupported(source));
	}

	private only360IfSupported(source: IVbSource): boolean {
		return !source.is360Enabled || this.VBrick360Util.is360PlaybackSupported(source);
	}

	//Todo - flashDisabled is always true. will clean it up later
	private onlySupportedPlaybackTypes(source: IVbSource, isFlashDisabled?: boolean, forceFlashHlsPlayback?: boolean): boolean {
		return this.NativePlaybackUtil.canPlayNatively(source) || //native playback OR
			this.HlsUtilService.isHtml5PlaybackSupported(source) || // HLS html5 playback is supported
			this.HlsUtilService.isMulticastPlaybackSupported(source.src, source.type) || // multicast HLS playback is supported
			this.vgConferenceDualHlsService.isPlaybackSupported(source); //source is dual conference hls on a supported environment
	}

	/**
	 * Merges playback options with matching labels together. Priority sequence among differing labels is maintained based on their first occurence. Matching label src values are sequenced based on their position in the input. This yields a menu with a single entry for each label with duplicates functioning as fallbacks behind the scenes.
	 * @param  Array<Object> supportedOptions   Raw input object array that has been filtered down to those videos capable of being played.
	 * @return Array<PlaybackOption>    Data used by this menu component, which has labels of the same value merged together.
	 */
	private shapeSupportedOptions(supportedOptions: IVbSource[]): PlaybackOption[] {
		const shapedOptions: PlaybackOption[] = [];

		supportedOptions.forEach(option => {
			const existing: PlaybackOption = shapedOptions.find(shaped => option.label === shaped.label);

			if (existing) { //existing playback option, so add src (will be a fallback)
				existing.addSrcOption(option);
			} else { //create a new playback option
				shapedOptions.push(new PlaybackOption(option));
			}
		});

		return shapedOptions;
	}
}
